export interface GlossaryEntry {
  /** Plain-language name shown first, before any jargon. */
  name: string;
  /** Industry abbreviation, when the term is known by one. */
  abbr?: string;
  /** One sentence a first-time investor can understand. */
  plain: string;
  /** How the number is built, in words rather than symbols. */
  formula?: string;
  /** What the number deliberately leaves out. */
  excludes?: string;
}

export type GlossaryTerm =
  | 'noi'
  | 'capex'
  | 'cashflow'
  | 'grossReturn'
  | 'netReturn'
  | 'equity'
  | 'ltv';

export const NOI_LABEL = 'Beneficio del alquiler';

export const glossary: Record<GlossaryTerm, GlossaryEntry> = {
  noi: {
    name: NOI_LABEL,
    abbr: 'NOI',
    plain:
      'Lo que te deja el piso después de pagar los gastos corrientes, antes de tocar la hipoteca.',
    formula: 'Alquiler cobrado − Gastos operativos (IBI, comunidad, seguro, suministros, gestión…)',
    excludes: 'No descuenta la cuota de la hipoteca ni las reformas.',
  },
  capex: {
    name: 'Inversión total',
    abbr: 'CAPEX',
    plain: 'Todo el dinero que te ha costado tener el inmueble, no solo lo que pagaste por él.',
    formula: 'Precio de compra + Gastos de adquisición + Reformas',
  },
  cashflow: {
    name: 'Cashflow',
    plain: 'El dinero que te queda realmente en el bolsillo cada mes.',
    formula: 'Alquiler cobrado − Gastos operativos − Cuota de la hipoteca',
  },
  grossReturn: {
    name: 'Rentabilidad bruta',
    plain:
      'Cuánto renta el alquiler sobre lo que te costó el inmueble, sin descontar ningún gasto.',
    formula: 'Alquiler anual ÷ Inversión total',
    excludes:
      'Al no descontar gastos ni hipoteca, sirve para comparar oportunidades, no para saber lo que ganas.',
  },
  netReturn: {
    name: 'Rentabilidad neta',
    plain: 'Lo mismo que la bruta, pero ya con los gastos corrientes descontados.',
    formula: `${NOI_LABEL} ÷ Inversión total`,
  },
  equity: {
    name: 'Equity',
    plain: 'La parte del valor del inmueble que ya es tuya y no del banco.',
    formula: 'Valor de mercado − Capital pendiente de la hipoteca',
  },
  ltv: {
    name: 'LTV',
    plain: 'Qué parte del valor del inmueble sigues debiendo al banco.',
    formula: 'Capital pendiente ÷ Valor de mercado',
  },
};

/** Label that leads with plain Spanish and keeps the industry term as a hint. */
export const glossaryLabel = (term: GlossaryTerm): string => {
  const entry = glossary[term];
  return entry.abbr ? `${entry.name} (${entry.abbr})` : entry.name;
};
